// Shared plumbing: API constants, auth, hooks, and all reusable components
// (Navbar, Footer, PageHero, AutoCarousel, LiveBadge, LiveAlertOptIn,
// TestimonyCarousel/Form, PlanYourVisit, admin GalleryPicker/EntityForm)
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Menu, X, Play, Heart, Users, Globe, MapPin, Clock, Facebook, Youtube, Instagram, Upload, Trash2, Image, Plus, Pencil, Copy } from "lucide-react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const LIVE_POLL_INTERVAL_MS = 60_000;
export const HTTP_UNAUTHORIZED = 401;
export const HTTP_FORBIDDEN = 403;

// ==================== IMAGE CONSTANTS ====================
export const PASTOR_EDDIE = "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/422wpxsv_IMG_9703.jpeg";
export const PASTOR_EDDIE_PREACHING = "/api/img-proxy/job_pdfbrowser-1/artifacts/pq8zim9t_IMG_9100.png";
export const PASTOR_YADIRA = "/api/img-proxy/job_pdfbrowser-1/artifacts/woacxdke_IMG_9099.png";

export const ABOUT_PASTORS_CAROUSEL = [
  "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/422wpxsv_IMG_9703.jpeg",
  "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/2mki0agm_image.png",
];

export const HOME_PASTOR_CAROUSEL = [
  "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/2mki0agm_image.png",
  "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/mzk9qbti_image.png",
  "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/7u8jz3xf_image.png",
  "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/9o39qm5n_received_943046930998990.jpeg",
];

export const WORSHIP_CAROUSEL = [
  "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/8u6xqhmy_4D235007-065C-4580-A3BE-1FF06A7A5994.PNG",
  "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/mslh9j97_20250702_204614.jpg",
];

export const HERO_BG = {
  about: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/2mki0agm_image.png",
  ministries: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/wode4y4t_Food%20group.jpeg",
  media: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/mzk9qbti_image.png",
  events: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/h696hjc1_received_870566991729900%20%281%29.jpeg",
  give: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/qxpzn7it_4D235007-065C-4580-A3BE-1FF06A7A5994.PNG",
  contact: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/uu9e1lzs_image.png",
};

export const WHCO_LOGO = "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/vu7pxnt2_whcho_site_header.png";
export const WHCO_ICON = "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/icvtqlqd_whcho_w_transparent_2.png";

export const SOCIAL_LINKS = {
  youtube: "https://www.youtube.com/@whconation9174",
  facebook: "https://www.facebook.com/whcoministries/",
  instagram: "https://instagram.com",
};

export const NAV_SECTIONS = [
  { title: "About Us", links: [
    { name: "Our Story", path: "/about" },
    { name: "Who We Are", path: "/who-we-are" },
    { name: "Leadership", path: "/about#leadership" },
    { name: "Plan Your Visit", path: "/contact#plan-your-visit" },
    { name: "Contact Us", path: "/contact" },
  ]},
  { title: "Resources", links: [
    { name: "Sermons", path: "/media" },
    { name: "Events", path: "/events" },
    { name: "Give", path: "/give" },
    { name: "YouTube", path: "https://www.youtube.com/@whconation9174", external: true },
  ]},
  { title: "Ministries", links: [
    { name: "Kingdom Academy", path: "/ministries#kingdom-academy" },
    { name: "Life Groups", path: "/ministries#life-groups" },
    { name: "Jesus Encounters", path: "/ministries#jesus-encounter" },
    { name: "Food Giveaway", path: "/ministries#food-giveaway" },
    { name: "Discipleship Training", path: "/ministries#discipleship" },
    { name: "Leadership Development", path: "/ministries#leadership" },
    { name: "Global Missions", path: "/ministries#missions" },
  ]},
];

// ==================== AUTH ====================
const AuthContext = React.createContext(null);
export const useAuth = () => React.useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { checkAuth(); }, []);

  const login = () => {
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      toast.success("Logged out successfully");
    } catch {
      toast.error("Logout failed");
    }
  };

  const value = React.useMemo(
    () => ({ user, loading, login, logout, checkAuth, setUser }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
          setUser(response.data);
          toast.success("Welcome back!");
          navigate('/admin', { replace: true, state: { user: response.data } });
        } catch {
          toast.error("Authentication failed");
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };
    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-white/60 text-sm tracking-widest uppercase">Signing you in...</p>
      </div>
    </div>
  );
};

// ==================== YOUTUBE LIVE ====================
export const useYouTubeLive = () => {
  const [live, setLive] = useState({ is_live: false, video_id: null, watch_url: null, title: null });
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/youtube/live-status`);
        if (!cancelled) setLive(r.data);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("youtube/live-status fetch failed (badge hidden):", err?.message || err);
        }
        if (!cancelled) setLive({ is_live: false, video_id: null, watch_url: null, title: null });
      }
    };
    check();
    const id = setInterval(check, LIVE_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return live;
};

export const LiveBadge = ({ className = "", showLabel = true }) => {
  const live = useYouTubeLive();
  if (!live.is_live || !live.watch_url) return null;
  return (
    <a
      href={live.watch_url}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white text-[10px] font-semibold uppercase tracking-[0.2em] rounded-sm hover:bg-red-700 transition-all ${className}`}
      data-testid="youtube-live-badge"
      title={live.title ? `Now Live: ${live.title}` : "We're live on YouTube!"}
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
      </span>
      {showLabel && "Live Now"}
    </a>
  );
};

// ==================== REUSABLE UI ====================
export const AutoCarousel = ({ images, alt = "", testId = "carousel", intervalMs = 5000, className = "" }) => {
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    if (images.length <= 1) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % images.length), intervalMs);
    return () => clearInterval(id);
  }, [images.length, intervalMs]);
  if (!images.length) return null;
  return (
    <div className={`relative aspect-[4/5] overflow-hidden bg-black shadow-2xl ${className}`} data-testid={testId}>
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={`${alt} ${i + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${i === slide ? "opacity-100" : "opacity-0"}`}
          data-testid={`${testId}-slide-${i}`}
        />
      ))}
      {images.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-3 z-10">
          {images.map((src, i) => (
            <button
              key={src}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${i === slide ? "bg-white w-8" : "bg-white/50 hover:bg-white/80 w-2"}`}
              data-testid={`${testId}-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const PageHero = ({ eyebrow, title, italic, bg, testId }) => (
  <section
    className="relative py-32 bg-black text-white overflow-hidden"
    style={{
      backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.75)), url('${bg}')`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}
  >
    <div className="relative max-w-4xl mx-auto px-8 text-center">
      <p className="heading-caps text-xs text-white/50 mb-4 tracking-[0.2em]">{eyebrow}</p>
      <h1 className="text-5xl md:text-6xl lg:text-7xl" style={{ fontFamily: "Playfair Display, serif" }} data-testid={testId}>
        {italic ? <span className="italic">{title}</span> : title}
      </h1>
    </div>
  </section>
);

export const ExpandableBio = ({ paragraphs, testId }) => {
  const [open, setOpen] = useState(false);
  const items = Array.isArray(paragraphs) ? paragraphs : [paragraphs];
  return (
    <div data-testid={`expandable-bio-${testId}`}>
      <p className="text-sm text-black/60 leading-relaxed">{items[0]}</p>
      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${open ? "max-h-[1000px] opacity-100 mt-4" : "max-h-0 opacity-0"}`}>
        {items.slice(1).map((para, i) => (
          <p key={`${testId}-para-${i}`} className="text-sm text-black/60 leading-relaxed mb-3 last:mb-0">{para}</p>
        ))}
      </div>
      {items.length > 1 && (
        <button
          onClick={() => setOpen((o) => !o)}
          className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-red-700 hover:text-red-900 transition-colors"
          data-testid={`expandable-bio-toggle-${testId}`}
        >
          {open ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
};

export const MinistrySlideshow = ({ images, ministryId }) => {
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % images.length), 5000);
    return () => clearInterval(id);
  }, [images.length]);
  return (
    <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: "16 / 9" }}>
      {images.map((src, i) => (
        <img key={src} src={src} alt={`${ministryId} slideshow ${i + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${i === slide ? "opacity-100" : "opacity-0"}`}
          data-testid={`${ministryId}-slide-${i}`}
        />
      ))}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3 z-10">
        {images.map((src, i) => (
          <button key={src} onClick={() => setSlide(i)} aria-label={`Go to slide ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${i === slide ? "bg-white w-8" : "bg-white/50 hover:bg-white/80 w-2"}`}
            data-testid={`${ministryId}-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
};

// ==================== LIVE ALERT OPT-IN ====================
export const LiveAlertOptIn = ({ variant = "dark", source = "web", eyebrow, title, subtitle, testId = "live-alert" }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/live-alerts/subscribe`, { email: email.trim(), name: name.trim() || null, source });
      setSubscribed(true);
      toast.success("You're on the list — we'll ping you when we go live.");
      setEmail(""); setName("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const isDark = variant === "dark";
  const wrap = isDark ? "bg-black text-white" : "bg-white text-black";
  const inputBase = isDark
    ? "bg-transparent border-b border-white/25 text-white placeholder-white/40 focus:border-white"
    : "bg-transparent border-b border-black/20 text-black placeholder-black/40 focus:border-black";
  const eyebrowCls = isDark ? "text-white/40" : "text-black/40";
  const subtitleCls = isDark ? "text-white/60" : "text-black/60";

  return (
    <div className={`p-10 md:p-14 ${wrap}`} data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600"></span>
        </span>
        <p className={`heading-caps text-xs tracking-[0.3em] ${eyebrowCls}`}>{eyebrow || "Live Alerts"}</p>
      </div>
      <h3 className="text-3xl md:text-4xl mb-4 leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
        {title || (<>Never miss when we go <span className="italic">live</span>.</>)}
      </h3>
      <p className={`text-sm md:text-base mb-8 max-w-lg ${subtitleCls}`}>
        {subtitle || "Drop your email and we'll send you a one-tap link the moment the Sunday service streams live on YouTube."}
      </p>

      {subscribed ? (
        <div className={`text-sm border-l-2 border-red-600 pl-4 py-2 ${isDark ? "text-white/80" : "text-black/80"}`} data-testid={`${testId}-success`}>
          You&apos;re on the list. See you on the live stream.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 sm:items-end" data-testid={`${testId}-form`}>
          <div className="flex-1 min-w-0">
            <label className={`block text-[10px] uppercase tracking-[0.25em] mb-2 ${eyebrowCls}`}>Name (optional)</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              className={`w-full px-0 py-3 focus:outline-none ${inputBase}`} placeholder="Your Name" data-testid={`${testId}-name`}
            />
          </div>
          <div className="flex-1 min-w-0">
            <label className={`block text-[10px] uppercase tracking-[0.25em] mb-2 ${eyebrowCls}`}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className={`w-full px-0 py-3 focus:outline-none ${inputBase}`} placeholder="you@example.com" required data-testid={`${testId}-email`}
            />
          </div>
          <button type="submit" disabled={submitting}
            className={isDark ? "btn-primary shrink-0 disabled:opacity-50" : "btn-dark shrink-0 disabled:opacity-50"}
            data-testid={`${testId}-submit`}
          >
            {submitting ? "Signing up..." : "Notify Me"}
          </button>
        </form>
      )}
    </div>
  );
};

// ==================== TESTIMONY ====================
export const TestimonyCarousel = () => {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/testimonies?limit=12`)
      .then((r) => { if (!cancelled) setItems(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (items.length <= 1) return;
    const id = setInterval(() => setIdx((n) => (n + 1) % items.length), 7000);
    return () => clearInterval(id);
  }, [items.length]);

  if (loading) return null;
  if (!items.length) return null;

  return (
    <section className="py-32 bg-white" data-testid="testimony-carousel">
      <div className="max-w-5xl mx-auto px-8 text-center">
        <p className="heading-caps text-xs text-red-700 mb-6 tracking-[0.3em]">Testimonies</p>
        <h2 className="text-4xl md:text-5xl mb-16 leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
          Stories of what God <span className="italic">is doing</span>.
        </h2>
        <div className="relative min-h-[280px]">
          {items.map((t, i) => (
            <blockquote key={t.testimony_id}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              data-testid={`testimony-slide-${i}`}
            >
              {t.title && <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.25em]">{t.title}</p>}
              <p className="text-xl md:text-2xl text-black/80 leading-relaxed italic mb-10 max-w-3xl mx-auto" style={{ fontFamily: "Playfair Display, serif" }}>
                &ldquo;{t.content}&rdquo;
              </p>
              <p className="text-sm text-black/50 tracking-[0.15em] uppercase">
                — {t.name}{t.location ? ` • ${t.location}` : ""}
              </p>
            </blockquote>
          ))}
        </div>
        {items.length > 1 && (
          <div className="flex justify-center gap-3 mt-14">
            {items.map((t, i) => (
              <button key={t.testimony_id} onClick={() => setIdx(i)} aria-label={`Testimony ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "bg-black w-8" : "bg-black/20 hover:bg-black/40 w-2"}`}
                data-testid={`testimony-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export const TestimonyForm = ({ variant = "light" }) => {
  const [form, setForm] = useState({ name: "", email: "", title: "", content: "", location: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const isDark = variant === "dark";
  const bg = isDark ? "bg-black text-white" : "bg-neutral-50 text-black";
  const inputCls = isDark
    ? "w-full px-0 py-3 bg-transparent border-b border-white/25 text-white placeholder-white/40 focus:border-white focus:outline-none"
    : "w-full px-0 py-3 bg-transparent border-b border-black/20 text-black placeholder-black/40 focus:border-black focus:outline-none";
  const eyebrowCls = isDark ? "text-white/40" : "text-black/40";
  const subtitleCls = isDark ? "text-white/60" : "text-black/60";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.content.trim()) {
      toast.error("Please share your name and testimony.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonies`, {
        name: form.name.trim(), email: form.email.trim() || null,
        title: form.title.trim() || null, content: form.content.trim(),
        location: form.location.trim() || null,
      });
      toast.success("Thank you for sharing! Your testimony is pending review.");
      setForm({ name: "", email: "", title: "", content: "", location: "" });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={`py-32 ${bg}`} data-testid="testimony-form-section">
      <div className="max-w-3xl mx-auto px-8">
        <p className={`heading-caps text-xs mb-4 tracking-[0.3em] ${eyebrowCls}`}>Share Your Story</p>
        <h2 className="text-4xl md:text-5xl mb-6 leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
          Tell us what God has <span className="italic">done</span>.
        </h2>
        <p className={`mb-12 leading-relaxed ${subtitleCls}`}>
          Answered prayer, an encounter, a breakthrough — your story builds faith in someone
          else. Share it below and, once approved, it may appear on the wall of testimonies.
        </p>
        {done ? (
          <div className={`border-l-2 border-red-600 pl-6 py-3 text-sm ${isDark ? "text-white/80" : "text-black/70"}`} data-testid="testimony-form-success">
            Testimony received. Our team will review and publish it soon — thank you.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="testimony-form">
            <div className="grid md:grid-cols-2 gap-6">
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls} placeholder="Your Name *" required data-testid="testimony-name"
              />
              <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                className={inputCls} placeholder="City (optional)" data-testid="testimony-location"
              />
            </div>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls} placeholder="Email (optional — so we can follow up)" data-testid="testimony-email"
            />
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={inputCls} placeholder="Title (e.g. 'Healed at Saturday Service')" data-testid="testimony-title"
            />
            <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
              className={`${inputCls} min-h-[160px] resize-y`} placeholder="Your testimony... *" required maxLength={4000} data-testid="testimony-content"
            />
            <div className="flex items-center justify-between gap-4 pt-2">
              <span className={`text-xs ${eyebrowCls}`}>{form.content.length}/4000</span>
              <button type="submit" disabled={submitting}
                className={isDark ? "btn-primary disabled:opacity-50" : "btn-dark disabled:opacity-50"}
                data-testid="testimony-submit"
              >
                {submitting ? "Sending..." : "Share Testimony"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

// ==================== NAV DRAWER + NAVBAR + FOOTER ====================
const NavDrawer = ({ open, onClose, user, login, logout }) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleLink = (link) => {
    onClose();
    if (link.external) { window.open(link.path, "_blank", "noopener,noreferrer"); return; }
    if (link.path.includes("#")) {
      const [pathname, hash] = link.path.split("#");
      navigate(pathname);
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } else {
      navigate(link.path);
    }
  };

  return (
    <>
      <div className={`fixed inset-0 z-[55] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose} data-testid="nav-drawer-overlay"
      />
      <aside className={`fixed top-0 right-0 z-[56] h-full w-full max-w-md bg-white text-black overflow-y-auto transition-transform duration-500 ease-out ${open ? "translate-x-0" : "translate-x-full"}`} data-testid="nav-drawer">
        <div className="flex items-center justify-between px-8 py-6 border-b border-black/10 sticky top-0 bg-white z-10">
          <Link to="/" onClick={onClose} className="flex items-center gap-3">
            <img src={WHCO_LOGO} alt="WHCO" className="h-10 w-auto" />
          </Link>
          <button onClick={onClose} className="p-2 hover:bg-black/5" data-testid="nav-drawer-close"><X size={22} /></button>
        </div>
        <div className="px-8 py-10 space-y-12">
          {NAV_SECTIONS.map((section) => (
            <div key={section.title}>
              <p className="heading-caps text-[10px] tracking-[0.25em] text-black/40 mb-5">{section.title}</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {section.links.map((link) => (
                  <button key={link.path + link.name} onClick={() => handleLink(link)}
                    className="text-left text-base hover:text-red-700 transition-colors duration-200"
                    style={{ fontFamily: "Playfair Display, serif" }}
                    data-testid={`nav-drawer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >{link.name}</button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <p className="heading-caps text-[10px] tracking-[0.25em] text-black/40 mb-5">Social Media</p>
            <div className="flex items-center gap-6">
              <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" className="text-black hover:text-red-700 transition-colors" data-testid="nav-drawer-youtube"><Youtube size={22} /></a>
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="text-black hover:text-red-700 transition-colors" data-testid="nav-drawer-facebook"><Facebook size={22} /></a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="text-black hover:text-red-700 transition-colors" data-testid="nav-drawer-instagram"><Instagram size={22} /></a>
            </div>
          </div>
          <div className="pt-8 border-t border-black/10 space-y-4">
            <button onClick={() => { onClose(); navigate("/contact?plan=1"); }}
              className="w-full bg-red-600 text-white px-6 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-3"
              data-testid="nav-drawer-plan-visit"
            ><Heart size={16} /> Plan Your Visit</button>
            {user ? (
              <div className="flex gap-3">
                <button onClick={() => { onClose(); navigate("/admin"); }} className="flex-1 border border-black/20 px-6 py-3 text-xs uppercase tracking-widest hover:bg-black/5" data-testid="nav-drawer-dashboard">Dashboard</button>
                <button onClick={() => { onClose(); logout(); }} className="px-6 py-3 border border-black/20 text-xs uppercase tracking-widest hover:bg-black/5" data-testid="nav-drawer-logout">Sign out</button>
              </div>
            ) : (
              <button onClick={() => { onClose(); login(); }} className="w-full border border-black/20 px-6 py-3 text-xs uppercase tracking-widest hover:bg-black/5" data-testid="nav-drawer-admin-login">Admin Login</button>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, login, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  const isHeroPage = location.pathname === "/";
  const onDark = isHeroPage && !scrolled;

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-white/95 backdrop-blur-sm shadow-sm" : isHeroPage ? "bg-transparent" : "bg-white"}`} data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <span className={`text-base md:text-lg tracking-[0.15em] uppercase font-semibold transition-colors duration-300 ${onDark ? "text-white" : "text-black"}`}
                style={{ fontFamily: "Playfair Display, serif", letterSpacing: "0.15em" }}
              >whco<span className="text-red-600">.</span>life</span>
            </Link>

            {!isHeroPage && (
              <div className="hidden lg:flex items-center gap-8">
                {[
                  { label: "About", path: "/about" },
                  { label: "Who We Are", path: "/who-we-are" },
                  { label: "Ministries", path: "/ministries" },
                  { label: "Sermons", path: "/media" },
                  { label: "Events", path: "/events" },
                  { label: "Give", path: "/give" },
                  { label: "Contact", path: "/contact" },
                ].map((t) => (
                  <Link key={t.path} to={t.path}
                    className={`text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${location.pathname === t.path ? "text-red-700" : "text-black hover:text-red-700"}`}
                    data-testid={`nav-tab-${t.label.toLowerCase()}`}
                  >{t.label}</Link>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 md:gap-4">
              <LiveBadge className="hidden sm:inline-flex" />
              <a href="https://give.tithe.ly/?formId=c68fa17a-6865-11ee-90fc-1260ab546d11"
                target="_blank" rel="noopener noreferrer" className="whco-nav-give" data-testid="nav-give-cta"
              >Give</a>
              <div className="hidden sm:flex items-center gap-1">
                <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube"
                  className={`p-2 transition-colors duration-300 ${onDark ? "text-white hover:text-red-400" : "text-black hover:text-red-700"}`}
                  data-testid="nav-social-youtube"><Youtube size={20} /></a>
                <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                  className={`p-2 transition-colors duration-300 ${onDark ? "text-white hover:text-red-400" : "text-black hover:text-red-700"}`}
                  data-testid="nav-social-facebook"><Facebook size={20} /></a>
                <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                  className={`p-2 transition-colors duration-300 ${onDark ? "text-white hover:text-red-400" : "text-black hover:text-red-700"}`}
                  data-testid="nav-social-instagram"><Instagram size={20} /></a>
              </div>
              <button onClick={() => setIsOpen(true)}
                className={`flex items-center gap-3 px-4 md:px-5 py-3 transition-colors duration-300 ${onDark ? "text-white hover:text-red-400" : "text-black hover:text-red-700"}`}
                data-testid="nav-menu-btn" aria-label="Open menu"
              >
                <span className="text-xs uppercase tracking-[0.25em] hidden md:inline">Menu</span>
                <Menu size={26} />
              </button>
            </div>
          </div>
        </div>
      </nav>
      <NavDrawer open={isOpen} onClose={() => setIsOpen(false)} user={user} login={login} logout={logout} />
    </>
  );
};

export const Footer = () => (
  <footer className="bg-black text-white py-20">
    <div className="max-w-7xl mx-auto px-8">
      <div className="grid md:grid-cols-4 gap-16 mb-16">
        <div className="md:col-span-2">
          <img src={WHCO_LOGO} alt="World Harvest Christian Outreach" className="h-16 w-auto mb-6" />
          <p className="text-white/60 text-sm leading-relaxed max-w-md">
            Making Disciples, Raising Up Leaders, Launching to the Nations.
            A community of believers transforming lives in Lancaster and beyond.
          </p>
        </div>
        <div>
          <h4 className="heading-caps text-xs mb-6 text-white/40">Service Times</h4>
          <div className="space-y-4 text-sm">
            <div><p className="text-white">Sunday</p><p className="text-white/60">10:00 AM – 12:00 PM</p></div>
            <div><p className="text-white">Wednesday</p><p className="text-white/60">7:00 PM</p></div>
          </div>
        </div>
        <div>
          <h4 className="heading-caps text-xs mb-6 text-white/40">Location</h4>
          <p className="text-sm text-white/60">224 W Lancaster Blvd<br />Lancaster, CA 93534</p>
          <div className="flex gap-4 mt-6">
            <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white transition-colors" data-testid="footer-facebook"><Facebook size={18} /></a>
            <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white transition-colors" data-testid="footer-youtube"><Youtube size={18} /></a>
            <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white transition-colors" data-testid="footer-instagram"><Instagram size={18} /></a>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-xs text-white/40">&copy; {new Date().getFullYear()} World Harvest Christian Outreach</p>
        <div className="flex gap-8">
          <Link to="/about" className="text-xs text-white/40 hover:text-white transition-colors">About</Link>
          <Link to="/contact" className="text-xs text-white/40 hover:text-white transition-colors">Contact</Link>
          <Link to="/give" className="text-xs text-white/40 hover:text-white transition-colors">Give</Link>
        </div>
      </div>
    </div>
  </footer>
);

// ==================== PLAN YOUR VISIT ====================
const SERVICE_OPTIONS = [
  { id: "sunday", label: "Sunday Morning Worship — 10:00 AM – 12:00 PM" },
  { id: "wednesday", label: "Wednesday Bible Study — 7:00 PM" },
];
const CHILDCARE_GRADES = ["Nursery (0-2)", "Preschool (3-4)", "Kindergarten", "1st Grade", "2nd Grade", "3rd Grade", "4th Grade", "5th Grade"];

export const PlanYourVisitForm = ({ onSuccess }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", visit_date: "", service_attending: "sunday",
    family_size: 1, family_members: "", needs_childcare: false, childcare_grade: "", questions: "",
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, family_size: Number(form.family_size) || 1 };
      if (!payload.needs_childcare) payload.childcare_grade = null;
      if (!payload.visit_date) delete payload.visit_date;
      await axios.post(`${API}/plan-your-visit`, payload);
      toast.success("We can't wait to meet you! See you Sunday.");
      setForm({ name: "", email: "", phone: "", visit_date: "", service_attending: "sunday", family_size: 1, family_members: "", needs_childcare: false, childcare_grade: "", questions: "" });
      setStep(1);
      if (onSuccess) onSuccess();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const sections = [{ id: 1, label: "About You" }, { id: 2, label: "Your Service" }, { id: 3, label: "Your Family" }, { id: 4, label: "Final Touches" }];

  return (
    <form onSubmit={submit} className="space-y-10" data-testid="plan-visit-form">
      <div className="flex gap-2 flex-wrap" data-testid="plan-visit-steps">
        {sections.map((s) => (
          <button type="button" key={s.id} onClick={() => setStep(s.id)}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-all ${step === s.id ? "bg-black text-white" : "bg-neutral-100 text-black hover:bg-neutral-200"}`}
            data-testid={`plan-visit-step-${s.id}`}
          >{s.id}. {s.label}</button>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-6">
          <h3 className="heading-caps text-xs text-black/40 tracking-[0.2em]">About You</h3>
          <input type="text" required value={form.name} onChange={(e) => update("name", e.target.value)}
            placeholder="Your full name *" className="w-full px-0 py-4 bg-transparent border-b border-black/20 focus:border-black focus:outline-none" data-testid="plan-visit-name"
          />
          <input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)}
            placeholder="Email address *" className="w-full px-0 py-4 bg-transparent border-b border-black/20 focus:border-black focus:outline-none" data-testid="plan-visit-email"
          />
          <input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)}
            placeholder="Phone (optional)" className="w-full px-0 py-4 bg-transparent border-b border-black/20 focus:border-black focus:outline-none" data-testid="plan-visit-phone"
          />
          <button type="button" onClick={() => setStep(2)} className="btn-dark" data-testid="plan-visit-next-1">Continue</button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <h3 className="heading-caps text-xs text-black/40 tracking-[0.2em]">Which Service Do You Plan to Attend?</h3>
          <div className="space-y-3">
            {SERVICE_OPTIONS.map((opt) => (
              <label key={opt.id}
                className={`flex items-center gap-4 p-5 border cursor-pointer transition-all ${form.service_attending === opt.id ? "border-black bg-black text-white" : "border-black/20 hover:border-black/40"}`}
                data-testid={`plan-visit-service-${opt.id}`}
              >
                <input type="radio" name="service" checked={form.service_attending === opt.id} onChange={() => update("service_attending", opt.id)} className="w-4 h-4" />
                <span className="text-base">{opt.label}</span>
              </label>
            ))}
          </div>
          <div>
            <label className="block heading-caps text-[10px] text-black/40 mb-2 tracking-[0.2em]">Date you&apos;ll visit (optional)</label>
            <input type="date" value={form.visit_date} onChange={(e) => update("visit_date", e.target.value)}
              className="w-full px-0 py-3 bg-transparent border-b border-black/20 focus:border-black focus:outline-none" data-testid="plan-visit-date"
            />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setStep(1)} className="px-6 py-3 border border-black/20 text-xs uppercase tracking-widest hover:bg-black/5">Back</button>
            <button type="button" onClick={() => setStep(3)} className="btn-dark" data-testid="plan-visit-next-2">Continue</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-6">
          <h3 className="heading-caps text-xs text-black/40 tracking-[0.2em]">Your Family</h3>
          <div>
            <label className="block heading-caps text-[10px] text-black/40 mb-2 tracking-[0.2em]">How many will be coming with you?</label>
            <input type="number" min={1} max={20} value={form.family_size} onChange={(e) => update("family_size", e.target.value)}
              className="w-32 px-0 py-3 bg-transparent border-b border-black/20 focus:border-black focus:outline-none text-2xl" data-testid="plan-visit-family-size"
              style={{ fontFamily: "Playfair Display, serif" }}
            />
          </div>
          <div>
            <label className="block heading-caps text-[10px] text-black/40 mb-2 tracking-[0.2em]">Names of those joining you</label>
            <textarea value={form.family_members} onChange={(e) => update("family_members", e.target.value)}
              placeholder="e.g. Maria (spouse), Luis (12), Sofia (8)" rows={3}
              className="w-full px-0 py-3 bg-transparent border-b border-black/20 focus:border-black focus:outline-none" data-testid="plan-visit-family-members"
            />
          </div>
          <div className="pt-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.needs_childcare} onChange={(e) => update("needs_childcare", e.target.checked)} className="w-4 h-4" data-testid="plan-visit-needs-childcare" />
              <span className="text-base">We&apos;ll need nursery / childcare</span>
            </label>
          </div>
          {form.needs_childcare && (
            <div>
              <label className="block heading-caps text-[10px] text-black/40 mb-2 tracking-[0.2em]">Which grade / age?</label>
              <select value={form.childcare_grade} onChange={(e) => update("childcare_grade", e.target.value)}
                className="w-full px-0 py-3 bg-transparent border-b border-black/20 focus:border-black focus:outline-none" data-testid="plan-visit-childcare-grade"
              >
                <option value="">— Select grade —</option>
                {CHILDCARE_GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          )}
          <div className="flex gap-3">
            <button type="button" onClick={() => setStep(2)} className="px-6 py-3 border border-black/20 text-xs uppercase tracking-widest hover:bg-black/5">Back</button>
            <button type="button" onClick={() => setStep(4)} className="btn-dark" data-testid="plan-visit-next-3">Continue</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-6">
          <h3 className="heading-caps text-xs text-black/40 tracking-[0.2em]">Anything we should know?</h3>
          <textarea value={form.questions} onChange={(e) => update("questions", e.target.value)}
            placeholder="Questions, prayer needs, accessibility requirements, or just say hi…" rows={5}
            className="w-full px-0 py-3 bg-transparent border-b border-black/20 focus:border-black focus:outline-none" data-testid="plan-visit-questions"
          />
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={() => setStep(3)} className="px-6 py-3 border border-black/20 text-xs uppercase tracking-widest hover:bg-black/5">Back</button>
            <button type="submit" disabled={submitting} className="btn-dark flex-1 disabled:opacity-50" data-testid="plan-visit-submit">
              {submitting ? "Sending..." : "Plan My Visit"}
            </button>
          </div>
        </div>
      )}
    </form>
  );
};

export const PlanYourVisitModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 fade-in" data-testid="plan-visit-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-8 py-6 border-b border-black/10 sticky top-0 bg-white z-10">
          <div>
            <p className="heading-caps text-[10px] text-red-700 tracking-[0.2em]">First time?</p>
            <h2 className="text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>Plan Your Visit</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/5" data-testid="plan-visit-close"><X size={20} /></button>
        </div>
        <div className="p-8"><PlanYourVisitForm onSuccess={onClose} /></div>
      </div>
    </div>
  );
};

export const FloatingPlanVisitButton = ({ onClick }) => {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  if (location.pathname === "/contact") return null;
  return (
    <button onClick={onClick}
      className="fixed bottom-8 right-8 z-40 flex items-center gap-3 bg-red-600 text-white px-6 py-4 shadow-2xl hover:bg-red-700 transition-all duration-300 hover:scale-105 group"
      data-testid="floating-plan-visit-btn"
    >
      <Heart size={18} className="group-hover:scale-110 transition-transform" />
      <span className="text-xs uppercase tracking-[0.2em] font-semibold">Plan Your Visit</span>
    </button>
  );
};

// ==================== ADMIN ENTITY FORM + GALLERY PICKER ====================
export const ENTITY_FIELDS = {
  events: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea", required: true },
    { name: "event_type", label: "Type", type: "select", required: true, options: ["service", "food_giveaway", "special_event", "community"] },
    { name: "date", label: "Start Date & Time", type: "datetime-local", required: true },
    { name: "end_date", label: "End Date & Time (optional)", type: "datetime-local" },
    { name: "location", label: "Location", type: "text" },
    { name: "image_url", label: "Image URL", type: "text" },
    { name: "is_recurring", label: "Recurring Event", type: "checkbox" },
  ],
  ministries: [
    { name: "name", label: "Name", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea", required: true },
    { name: "category", label: "Category", type: "select", required: true, options: ["food_outreach", "discipleship", "leadership", "missions", "children", "youth", "worship"] },
    { name: "image_url", label: "Image URL", type: "text" },
    { name: "contact_email", label: "Contact Email", type: "email" },
    { name: "is_active", label: "Active", type: "checkbox" },
  ],
  media: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea" },
    { name: "media_type", label: "Type", type: "select", required: true, options: ["video", "audio", "livestream"] },
    { name: "url", label: "URL (YouTube/Facebook/File)", type: "text", required: true },
    { name: "thumbnail_url", label: "Thumbnail URL", type: "text" },
    { name: "speaker", label: "Speaker", type: "text" },
    { name: "date", label: "Date", type: "datetime-local", required: true },
    { name: "duration", label: "Duration (e.g. 42:15)", type: "text" },
    { name: "is_featured", label: "Featured", type: "checkbox" },
  ],
};

export const ENTITY_SINGULAR = { events: "Event", ministries: "Ministry", media: "Media" };

const toDateTimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const GalleryPicker = ({ open, onClose, onPick }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/gallery`, { withCredentials: true })
      .then((r) => { if (!cancelled) setImages(r.data || []); })
      .catch(() => { if (!cancelled) setImages([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;
  const filtered = filter === "all" ? images : images.filter((g) => g.category === filter);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" data-testid="gallery-picker-modal">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-black/10">
          <h2 className="text-xl" style={{ fontFamily: "Playfair Display, serif" }}>Select an Image</h2>
          <button onClick={onClose} className="p-2 hover:bg-black/5" data-testid="gallery-picker-close"><X size={20} /></button>
        </div>
        <div className="px-6 py-3 border-b border-black/10 flex flex-wrap gap-2">
          {["all", "general", "worship", "events", "ministries", "pastors"].map((cat) => {
            const count = cat === "all" ? images.length : images.filter((g) => g.category === cat).length;
            const active = filter === cat;
            return (
              <button key={cat} onClick={() => setFilter(cat)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider transition-all ${active ? "bg-black text-white" : "bg-neutral-100 text-black/60 hover:bg-neutral-200"}`}
                data-testid={`picker-filter-${cat}`}
              >{cat} <span className="opacity-50 ml-1">{count}</span></button>
            );
          })}
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <p className="text-center text-black/40 py-12">Loading…</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <Image size={40} className="mx-auto text-black/20 mb-3" />
              <p className="text-black/40 text-sm">{images.length === 0 ? "No uploaded images yet. Upload images from the Gallery tab first." : `No images in "${filter}"`}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map((img) => (
                <button key={img.image_id} type="button" onClick={() => { onPick(img.url); onClose(); }}
                  className="relative group bg-neutral-100 overflow-hidden aspect-square hover:ring-2 hover:ring-black transition-all"
                  data-testid={`picker-item-${img.image_id}`}
                >
                  <img src={img.url} alt={img.filename} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 text-white text-xs uppercase tracking-widest font-semibold">Select</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const EntityForm = ({ entityType, initial, onClose, onSaved }) => {
  const fields = ENTITY_FIELDS[entityType];
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFieldName, setPickerFieldName] = useState(null);
  const [form, setForm] = useState(() => {
    const seed = {};
    fields.forEach((f) => {
      let val = initial?.[f.name];
      if (f.type === "datetime-local") val = toDateTimeLocal(val);
      if (f.type === "checkbox") val = val ?? (f.name === "is_active" ? true : false);
      seed[f.name] = val ?? (f.type === "checkbox" ? false : "");
    });
    return seed;
  });

  const isEdit = Boolean(initial);
  const idKey = entityType === "events" ? "event_id" : entityType === "ministries" ? "ministry_id" : "media_id";
  const handleChange = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "datetime-local") payload[f.name] = payload[f.name] ? new Date(payload[f.name]).toISOString() : null;
        if ((f.type === "text" || f.type === "textarea" || f.type === "email") && payload[f.name] === "") {
          if (f.required) return;
          delete payload[f.name];
        }
      });

      if (isEdit) {
        await axios.put(`${API}/admin/${entityType}/${initial[idKey]}`, payload, { withCredentials: true });
        toast.success(`${ENTITY_SINGULAR[entityType]} updated`);
      } else {
        await axios.post(`${API}/admin/${entityType}`, payload, { withCredentials: true });
        toast.success(`${ENTITY_SINGULAR[entityType]} created`);
      }
      onSaved();
      onClose();
    } catch (error) {
      const msg = error.response?.data?.detail || `Failed to save ${ENTITY_SINGULAR[entityType].toLowerCase()}`;
      toast.error(typeof msg === "string" ? msg : "Validation error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="entity-form-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-8 py-6 border-b border-black/10">
          <h2 className="text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>{isEdit ? "Edit" : "New"} {ENTITY_SINGULAR[entityType]}</h2>
          <button onClick={onClose} className="p-2 hover:bg-black/5" data-testid="entity-form-close"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-8 space-y-5">
          {fields.map((f) => (
            <div key={f.name}>
              <label className="block text-xs uppercase tracking-widest text-black/60 mb-2">
                {f.label}{f.required && <span className="text-red-600"> *</span>}
              </label>
              {f.type === "textarea" ? (
                <textarea required={f.required} value={form[f.name] || ""} onChange={(e) => handleChange(f.name, e.target.value)}
                  rows={4} className="w-full px-4 py-3 border border-black/20 focus:border-black outline-none text-sm" data-testid={`entity-field-${f.name}`}
                />
              ) : f.type === "select" ? (
                <select required={f.required} value={form[f.name] || ""} onChange={(e) => handleChange(f.name, e.target.value)}
                  className="w-full px-4 py-3 border border-black/20 focus:border-black outline-none text-sm bg-white" data-testid={`entity-field-${f.name}`}
                >
                  <option value="">— Select —</option>
                  {f.options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
                </select>
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={Boolean(form[f.name])} onChange={(e) => handleChange(f.name, e.target.checked)} className="w-4 h-4" data-testid={`entity-field-${f.name}`} />
                  <span className="text-sm text-black/60">Enable</span>
                </label>
              ) : (
                <div className="space-y-2">
                  <input type={f.type} required={f.required} value={form[f.name] || ""} onChange={(e) => handleChange(f.name, e.target.value)}
                    className="w-full px-4 py-3 border border-black/20 focus:border-black outline-none text-sm" data-testid={`entity-field-${f.name}`}
                  />
                  {f.name === "image_url" && (
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => { setPickerFieldName(f.name); setPickerOpen(true); }}
                        className="flex items-center gap-2 text-xs uppercase tracking-widest text-black/60 hover:text-black px-3 py-1.5 border border-black/20 hover:border-black"
                        data-testid={`entity-field-${f.name}-pick`}
                      ><Image size={14} /> Pick from Gallery</button>
                      {form[f.name] && <img src={form[f.name]} alt="preview" className="h-12 w-12 object-cover border border-black/10" />}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-3 pt-4">
            <button type="submit" disabled={saving} className="btn-dark flex-1 disabled:opacity-50" data-testid="entity-form-submit">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create"}
            </button>
            <button type="button" onClick={onClose} className="px-6 py-3 border border-black/20 text-xs uppercase tracking-widest hover:bg-black/5" data-testid="entity-form-cancel">Cancel</button>
          </div>
        </form>
      </div>
      <GalleryPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={(url) => pickerFieldName && handleChange(pickerFieldName, url)} />
    </div>
  );
};

// Re-export lucide icons used by pages so pages don't need to import lucide-react directly
export { Menu, X, Play, Heart, Users, Globe, MapPin, Clock, Facebook, Youtube, Instagram, Upload, Trash2, Image, Plus, Pencil, Copy, Link };
export { axios, toast };
